import type { ReactNode } from "react";

import type { TooltipColor } from "@/web/components/primitives/tooltip";
import { type SmbActionCategory } from "@/web/routes/_protected/smb/-dashboard/-types";

export type TourStepId =
  | "welcome"
  | "fundraise"
  | "budget"
  | "negotiate"
  | "categoryFilter"
  | "swipeToSkip"
  | "chooseCategory";

export type ActionFilterCategory = Exclude<SmbActionCategory, "generic"> | "all";

export type ModalPage = {
  title: string;
  body: ReactNode;
};

export type CategoryChoice = {
  label: string;
  value: ActionFilterCategory;
  color: "blue" | "yellow" | "red" | undefined;
};

export type TourStep =
  | {
      kind: "modal";
      id: TourStepId;
      pages: readonly ModalPage[];
    }
  | {
      kind: "tooltip";
      id: TourStepId;
      side: "top" | "bottom" | "left" | "right";
      // Defaults to the primary/foreground tooltip styling when omitted.
      color?: TooltipColor;
      title: string;
      body: ReactNode;
    }
  | {
      kind: "choice";
      id: TourStepId;
      title: string;
      body: ReactNode;
      choices: readonly CategoryChoice[];
    };

export const TOUR_STEPS: readonly TourStep[] = [
  {
    kind: "modal",
    id: "welcome",
    pages: [
      {
        title: "The Journey to Ownership",
        body: (
          <ol className="mx-auto my-4 max-w-[90%] list-decimal space-y-4 text-xl font-bold">
            <li className="space-y-2">
              <div>Plan</div>
              <p className="text-base font-normal">
                Know your budget. Price the building. Make your offer.
              </p>
            </li>
            <li className="text-muted-foreground">
              <div>Rally</div>
              <p className="text-base font-normal">
                Sign the deal. Bring your community in as investors.
              </p>
            </li>
            <li className="text-muted-foreground">
              <div>Own</div>
              <p className="text-base font-normal">
                Get rewarded for stewarding the building for you and yours.
              </p>
            </li>
          </ol>
        ),
      },
      // {
      //   title: "The withus platform",
      //   body: (
      //     <div className="mx-auto my-4 max-w-[90%] space-y-4 text-base">
      //       <p>
      //         The journey to ownership comprises three key categories: <strong>fundraise</strong>,{" "}
      //         <strong>budget</strong>, and <strong>negotiate</strong>.
      //       </p>
      //       <p>Throughout the app,</p>
      //       <ul className="list-disc">
      //         <li>
      //           <span className="bg-withus-yellow/40 px-1">Fundraise</span> actions gather social
      //           signal from would-be investors.
      //         </li>
      //         <li>
      //           <span className="bg-withus-blue/40 px-1">Budget</span> actions estimate the amount
      //           you can afford to spend on a property.
      //         </li>
      //         <li>
      //           <span className="bg-withus-red/40 px-1">Negotiate</span> actions establish a
      //           purchase price and set the terms of a potential deal.
      //         </li>
      //       </ul>
      //     </div>
      //   ),
      // },
    ],
  },
  {
    kind: "tooltip",
    id: "fundraise",
    side: "top",
    color: "yellow",
    title: "Fund",
    body: (
      <p>Stack your investment, seller financing, and investor capital to meet the deal price.</p>
    ),
  },
  {
    kind: "tooltip",
    id: "budget",
    side: "top",
    color: "blue",
    title: "Budget",
    body: (
      <p>
        Connect your financial tools to learn what you can afford to pay — and what return you can
        offer investors.
      </p>
    ),
  },
  {
    kind: "tooltip",
    id: "negotiate",
    side: "top",
    color: "red",
    title: "Price",
    body: (
      <p>
        Ground your offer in comparable sales, what your lease supports, and what the landlord
        wants.
      </p>
    ),
  },
  {
    kind: "tooltip",
    id: "categoryFilter",
    side: "bottom",
    title: "Action list",
    body: <p>Filter your deck to focus on one lever at a time.</p>,
  },
  // Anchored to the deck container rather than the front card: the front card
  // changes identity as cards are skipped, so a card-level anchor would unmount
  // mid-tour if the user starts swiping.
  //
  // "It'll come back" is the load-bearing half of this copy. Skipping recycles
  // the card to the back of the deck instead of dismissing it, and without
  // saying so the loop reads as a bug rather than a deliberate defer.
  {
    kind: "tooltip",
    id: "swipeToSkip",
    side: "top",
    title: "Not right now?",
    body: <p>Swipe a card left to skip it — it'll come back around later.</p>,
  },
  // {
  //   kind: "choice",
  //   id: "chooseCategory",
  //   title: "Where would you like to start?",
  //   body: (
  //     <p className="text-center text-muted-foreground">
  //       Pick a category to focus on first, or see every card at once.
  //     </p>
  //   ),
  //   choices: [
  //     { label: "All cards", value: "all", color: undefined },
  //     { label: "Fundraising", value: "fundraise", color: "yellow" },
  //     { label: "Budgeting", value: "budget", color: "blue" },
  //     { label: "Negotiation", value: "negotiate", color: "red" },
  //   ],
  // },
] as const;
